/**
 * GA4 Enhanced Ecommerce tracking for product recommendation sections.
 * Sends view_item_list, select_item, and add_to_cart events directly
 * to GA4 via gtag().
 *
 * Handles two section types:
 *   - Complementary products ([data-complementary-products]) on product pages
 *   - Cross-sells ([data-cross-sells]) in the cart drawer
 *
 * Uses sessionStorage to carry list attribution when a user clicks a
 * complementary product and later adds to cart from the product page.
 */
(function () {
  'use strict';

  var STORAGE_KEY = '_rec_attribution';

  var SELECTORS = {
    complementary: '[data-complementary-products]',
    crossSells: '[data-cross-sells]',
    productItem: '[data-input-item]',
    crossSellItem: '.cross-sells__item',
    crossSellAddBtn: '.cross-sells__item-add'
  };

  // Track which list IDs have already fired view_item_list on this page load
  // to prevent duplicate events from cart drawer re-renders.
  var viewedLists = {};

  function getSourceProduct(container) {
    var productId = container.getAttribute('data-product-id');
    if (!productId) return {};
    var productName = '';
    if (window.meta && window.meta.product) {
      productName = window.meta.product.name || '';
    }
    if (!productName) {
      var titleEl = document.querySelector('h1');
      if (titleEl) productName = titleEl.textContent.replace(/\s+/g, ' ').trim();
    }
    return {
      source_product_id: productId,
      source_product_name: productName
    };
  }

  function buildItemFromProductItem(el, index) {
    return {
      item_id: el.getAttribute('data-id') || '',
      item_name: el.getAttribute('data-title') || '',
      price: parseFloat(el.getAttribute('data-price')) || 0,
      item_brand: el.getAttribute('data-vendor') || '',
      item_variant: el.getAttribute('data-sku') || '',
      item_category: el.getAttribute('data-type') || '',
      quantity: 1,
      index: index
    };
  }

  function buildItemFromCrossSell(el, index) {
    var titleEl = el.querySelector('.cross-sells__item-title a, .cross-sells__item-title');
    var priceText = el.getAttribute('data-price');
    return {
      item_id: el.getAttribute('data-product-id') || '',
      item_name: titleEl ? titleEl.textContent.trim() : '',
      price: parseFloat(priceText) || 0,
      item_brand: el.getAttribute('data-vendor') || '',
      item_variant: el.getAttribute('data-sku') || '',
      item_category: el.getAttribute('data-type') || '',
      quantity: 1,
      index: index
    };
  }

  function getListInfo(container) {
    return {
      item_list_name: container.getAttribute('data-item-list-name') || '',
      item_list_id: container.getAttribute('data-item-list-id') || ''
    };
  }

  function collectItems(container, isComplementary) {
    var selector = isComplementary ? SELECTORS.productItem : SELECTORS.crossSellItem;
    var elements = container.querySelectorAll(selector);
    var items = [];
    for (var i = 0; i < elements.length; i++) {
      var item = isComplementary
        ? buildItemFromProductItem(elements[i], i)
        : buildItemFromCrossSell(elements[i], i);
      var listInfo = getListInfo(container);
      item.item_list_name = listInfo.item_list_name;
      item.item_list_id = listInfo.item_list_id;
      items.push(item);
    }
    return items;
  }

  function pushViewItemList(container, isComplementary) {
    var listInfo = getListInfo(container);
    var listId = listInfo.item_list_id;

    // Debounce: only fire once per list ID per page load.
    // Prevents duplicate events from cart drawer DOM re-renders.
    if (viewedLists[listId]) return;
    viewedLists[listId] = true;

    var items = collectItems(container, isComplementary);
    if (items.length === 0) return;
    var eventParams = {
      item_list_name: listInfo.item_list_name,
      item_list_id: listId,
      items: items
    };
    if (isComplementary) {
      var source = getSourceProduct(container);
      eventParams.source_product_id = source.source_product_id || '';
      eventParams.source_product_name = source.source_product_name || '';
    }
    gtag('event', 'view_item_list', eventParams);
  }

  function setupViewTracking(container, isComplementary) {
    if (!('IntersectionObserver' in window)) {
      pushViewItemList(container, isComplementary);
      return;
    }
    var observer = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting) {
          pushViewItemList(entries[i].target, isComplementary);
          observer.unobserve(entries[i].target);
        }
      }
    }, { threshold: 0.5 });
    observer.observe(container);
  }

  // --- sessionStorage attribution ---

  function storeAttribution(listInfo, item, sourceProduct) {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
        item_list_name: listInfo.item_list_name,
        item_list_id: listInfo.item_list_id,
        item_id: item.item_id,
        source_product_id: sourceProduct.source_product_id || '',
        source_product_name: sourceProduct.source_product_name || '',
        timestamp: Date.now()
      }));
    } catch (e) {
      // sessionStorage unavailable (private browsing etc.) — silently skip
    }
  }

  function getAttribution() {
    try {
      var raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      var data = JSON.parse(raw);
      // Expire after 30 minutes
      if (Date.now() - data.timestamp > 30 * 60 * 1000) {
        sessionStorage.removeItem(STORAGE_KEY);
        return null;
      }
      return data;
    } catch (e) {
      return null;
    }
  }

  function clearAttribution() {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      // ignore
    }
  }

  function setupClickTracking(container, isComplementary) {
    container.addEventListener('click', function (e) {
      var link = e.target.closest('a[href]');
      if (!link) return;

      var itemEl = isComplementary
        ? link.closest(SELECTORS.productItem)
        : link.closest(SELECTORS.crossSellItem);
      if (!itemEl) return;

      var index = 0;
      var selector = isComplementary ? SELECTORS.productItem : SELECTORS.crossSellItem;
      var siblings = container.querySelectorAll(selector);
      for (var i = 0; i < siblings.length; i++) {
        if (siblings[i] === itemEl) { index = i; break; }
      }

      var item = isComplementary
        ? buildItemFromProductItem(itemEl, index)
        : buildItemFromCrossSell(itemEl, index);

      var listInfo = getListInfo(container);
      item.item_list_name = listInfo.item_list_name;
      item.item_list_id = listInfo.item_list_id;

      var eventParams = {
        item_list_name: listInfo.item_list_name,
        item_list_id: listInfo.item_list_id,
        items: [item]
      };
      if (isComplementary) {
        var source = getSourceProduct(container);
        eventParams.source_product_id = source.source_product_id || '';
        eventParams.source_product_name = source.source_product_name || '';
      }
      gtag('event', 'select_item', eventParams);

      // Store attribution in sessionStorage so the product page can
      // fire an attributed add_to_cart event.
      if (isComplementary) {
        storeAttribution(listInfo, item, source);
      }
    });
  }

  // Registered ONCE at document level in the capture phase, not per container.
  // dynamic-cross-sells.js handles Add clicks on hydrated items with a
  // capture-phase listener that calls stopImmediatePropagation(), which kills
  // any bubble-phase listener on the container — dynamic add-to-carts were
  // invisible to GA. This script's tag precedes dynamic-cross-sells.js in
  // theme.liquid, so this capture listener registers (and therefore fires)
  // first and always sees the click.
  function setupGlobalAddToCartTracking() {
    document.addEventListener('click', function (e) {
      if (!e.target || !e.target.closest) return;
      var btn = e.target.closest(SELECTORS.crossSellAddBtn);
      if (!btn) return;

      var itemEl = btn.closest(SELECTORS.crossSellItem);
      if (!itemEl) return;

      var container = btn.closest(SELECTORS.crossSells);
      if (!container) return;

      var index = 0;
      var siblings = container.querySelectorAll(SELECTORS.crossSellItem);
      for (var i = 0; i < siblings.length; i++) {
        if (siblings[i] === itemEl) { index = i; break; }
      }

      var item = buildItemFromCrossSell(itemEl, index);
      var listInfo = getListInfo(container);
      item.item_list_name = listInfo.item_list_name;
      item.item_list_id = listInfo.item_list_id;

      gtag('event', 'add_to_cart', {
        item_list_name: listInfo.item_list_name,
        item_list_id: listInfo.item_list_id,
        items: [item]
      });
    }, true);
  }

  // --- Product page: attributed add_to_cart from sessionStorage ---

  function setupProductPageAttribution() {
    var attribution = getAttribution();
    if (!attribution) return;

    // Find the current product ID from the product JSON or the page container
    var currentProductId = null;
    var productJsonEl = document.querySelector('script[id^="ProductJson-"]');
    if (productJsonEl) {
      try {
        var productData = JSON.parse(productJsonEl.textContent);
        currentProductId = String(productData.id);
      } catch (e) {
        // ignore parse errors
      }
    }
    if (!currentProductId) return;

    // Only apply attribution if this is the product that was clicked
    if (attribution.item_id !== currentProductId) return;

    // Listen for clicks on the add-to-cart button. This is purely additive —
    // it fires a GA4 event but does not touch the cart/fetch logic at all.
    var addToCartBtn = document.querySelector('[data-add-to-cart]');
    if (!addToCartBtn) return;

    var attributed = false;
    addToCartBtn.addEventListener('click', function () {
      if (attributed) return;
      attributed = true;

      var productName = '';
      var productPrice = 0;
      var productBrand = '';
      var productSku = '';
      var productType = '';

      var jsonEl = document.querySelector('script[id^="ProductJson-"]');
      if (jsonEl) {
        try {
          var data = JSON.parse(jsonEl.textContent);
          productName = data.title || '';
          productPrice = (data.price || 0) / 100;
          productBrand = data.vendor || '';
          productType = data.product_type || '';
          if (data.variants && data.variants.length > 0) {
            productSku = data.variants[0].sku || '';
          }
        } catch (e) {
          // ignore
        }
      }

      gtag('event', 'add_to_cart', {
        item_list_name: attribution.item_list_name,
        item_list_id: attribution.item_list_id,
        source_product_id: attribution.source_product_id,
        source_product_name: attribution.source_product_name,
        items: [{
          item_id: currentProductId,
          item_name: productName,
          price: productPrice,
          item_brand: productBrand,
          item_variant: productSku,
          item_category: productType,
          quantity: 1,
          item_list_name: attribution.item_list_name,
          item_list_id: attribution.item_list_id
        }]
      });

      clearAttribution();
    });
  }

  function initContainer(container, isComplementary) {
    if (container.hasAttribute('data-rec-tracking-init')) return;
    container.setAttribute('data-rec-tracking-init', 'true');
    setupViewTracking(container, isComplementary);
    setupClickTracking(container, isComplementary);
  }

  function initAll() {
    var compContainers = document.querySelectorAll(SELECTORS.complementary);
    for (var i = 0; i < compContainers.length; i++) {
      initContainer(compContainers[i], true);
    }
    var csContainers = document.querySelectorAll(SELECTORS.crossSells);
    for (var j = 0; j < csContainers.length; j++) {
      initContainer(csContainers[j], false);
    }
  }

  setupGlobalAddToCartTracking();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
      setupProductPageAttribution();
    });
  } else {
    initAll();
    setupProductPageAttribution();
  }

  var observer = new MutationObserver(function (mutations) {
    var shouldReinit = false;
    for (var i = 0; i < mutations.length; i++) {
      for (var j = 0; j < mutations[i].addedNodes.length; j++) {
        var node = mutations[i].addedNodes[j];
        if (node.nodeType === 1) {
          if (node.matches && (node.matches(SELECTORS.complementary) || node.matches(SELECTORS.crossSells))) {
            shouldReinit = true;
            break;
          }
          if (node.querySelector && (node.querySelector(SELECTORS.complementary) || node.querySelector(SELECTORS.crossSells))) {
            shouldReinit = true;
            break;
          }
        }
      }
      if (shouldReinit) break;
    }
    if (shouldReinit) initAll();
  });
  observer.observe(document.body || document.documentElement, { childList: true, subtree: true });
})();
